import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  RefreshControl,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { COLORS, FONTS } from '../../constants/config';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageContainer, Breadcrumb } from '../../components';
import { archivesApi, ArchiveItem } from '../../services/apiService';

/**
 * سلة المحذوفات — DTS chapter 10.
 *
 * Lists soft-deleted items (the live row was removed but a snapshot
 * lives in `Archive`). Strategy Team / Tech Admin can RESTORE an item
 * before its 30-day expiry. After expiry the row is purged (the actual
 * purge job lives in the scheduler — to be added).
 *
 * Per DTS:
 *   - "لا يوجد حذف نهائي في النظام — جميع البيانات تبقى مؤقتاً في
 *     قاعدة البيانات. العناصر المحذوفة تظهر في سلة المحذوفات للمسؤول
 *     التقني وفريق الاستراتيجية. الاستعادة ممكنة في أي وقت."
 */

const CONTENT_TYPE_LABELS: Record<string, string> = {
  strategic_goal: 'هدف استراتيجي',
  initiative: 'عنصر تشغيلي',
  activity: 'نشاط',
  kpi: 'مؤشر أداء',
  kpi_value: 'قراءة مؤشر',
  process: 'عملية',
  subprocess: 'عملية فرعية',
  process_step: 'خطوة عملية',
  service: 'خدمة',
  risk: 'خطر',
  organizational_unit: 'وحدة تنظيمية',
  unit_goal: 'هدف وحدة تنظيمية',
  goal_comment: 'تعليق على هدف',
  initiative_comment: 'تعليق على مبادرة',
  kpi_comment: 'تعليق على مؤشر',
  service_comment: 'تعليق على خدمة',
  unit_goal_comment: 'تعليق على هدف وحدة',
  note: 'ملاحظة',
  attachment: 'مرفق',
  evidence: 'دليل',
  employee: 'موظف',
};

const PAGE_SIZE = 20;

export default function RecycleBinScreen() {
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const { user } = useAuth();

  const canRestore = !!user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.strategic_goals.create');

  const [items, setItems] = useState<ArchiveItem[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState('');

  // Confirm-restore modal
  const [restoring, setRestoring] = useState<ArchiveItem | null>(null);
  const [restoringInFlight, setRestoringInFlight] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (contentTypeFilter) params.content_type = contentTypeFilter;

      const resp = await archivesApi.list(params);
      setItems(resp.results || []);
      setCount(resp.count || 0);
    } catch (err: any) {
      console.error('Failed to load archives:', err);
      showError('تعذّر تحميل سلة المحذوفات');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, search, contentTypeFilter, showError]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));

  useEffect(() => { setPage(1); }, [search, contentTypeFilter]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const handleRestore = async (archive: ArchiveItem) => {
    setRestoringInFlight(true);
    try {
      await archivesApi.restore(archive.id);
      showSuccess('تمت الاستعادة بنجاح');
      setRestoring(null);
      await fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.error
        || err?.response?.data?.detail
        || 'فشل في الاستعادة';
      showError(msg);
    } finally {
      setRestoringInFlight(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('ar', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    inputBackground: colors.inputBackground,
    border: colors.border,
  };

  return (
    <PageContainer>
      <ScrollView
        contentContainerStyle={{ padding: 16, gap: 16 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData(); }} />
        }
      >
        <Breadcrumb items={[
          { label: 'الرئيسية', icon: 'home' as any },
          { label: 'سلة المحذوفات' },
        ]} />

        {/* Header */}
        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.title, { color: themeColors.textPrimary }]}>سلة المحذوفات</Text>
            <Text style={[styles.subtitle, { color: themeColors.textMuted }]}>
              العناصر المحذوفة تُحفظ هنا لمدة 30 يوم. خلال هذه المدة يمكن استعادتها.
              بعدها يتم الحذف النهائي تلقائياً.
            </Text>
            <Text style={[styles.subtitle, { color: themeColors.textMuted, marginTop: 4 }]}>
              إجمالي: <Text style={{ fontFamily: FONTS.medium }}>{count}</Text> عنصر محذوف
            </Text>
          </View>
        </View>

        {/* Filters */}
        <View style={[styles.filterCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={styles.filterRow}>
            <View style={styles.filterField}>
              <Text style={[styles.filterLabel, { color: themeColors.textMuted }]}>بحث</Text>
              <TextInput
                value={search}
                onChangeText={setSearch}
                placeholder="ابحث في عنوان العنصر..."
                placeholderTextColor={themeColors.textMuted}
                style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                textAlign="right"
              />
            </View>

            <View style={styles.filterField}>
              <Text style={[styles.filterLabel, { color: themeColors.textMuted }]}>نوع العنصر</Text>
              <View style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, padding: 0 }]}>
                <select
                  value={contentTypeFilter}
                  onChange={(e) => setContentTypeFilter(e.target.value)}
                  style={{
                    width: '100%', height: 44, padding: '0 12px',
                    background: 'transparent', border: 'none', outline: 'none',
                    color: themeColors.textPrimary as string,
                    fontFamily: FONTS.regular as any, fontSize: 14,
                    textAlign: 'right', direction: 'rtl',
                  }}
                >
                  <option value="">الكل</option>
                  {Object.entries(CONTENT_TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </View>
            </View>
          </View>
        </View>

        {/* List */}
        {loading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : items.length === 0 ? (
          <View style={[styles.emptyCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>
              السلة فارغة — لا توجد عناصر محذوفة
            </Text>
          </View>
        ) : (
          <View style={{ gap: 12 }}>
            {items.map((item) => {
              const expiringSoon = item.days_until_expiry <= 7;
              return (
                <View
                  key={item.id}
                  style={[styles.itemCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                >
                  <View style={{ flex: 1, gap: 6 }}>
                    <View style={styles.itemHeaderRow}>
                      <View style={styles.typeBadge}>
                        <Text style={styles.typeBadgeText}>
                          {CONTENT_TYPE_LABELS[item.content_type] || item.content_type}
                        </Text>
                      </View>
                      <Text style={[styles.itemTitle, { color: themeColors.textPrimary }]} numberOfLines={1}>
                        {item.object_title || `عنصر #${item.object_id}`}
                      </Text>
                    </View>

                    {item.organization_name && (
                      <Text style={[styles.itemMeta, { color: themeColors.textMuted }]}>
                        الجهة: {item.organization_name}
                      </Text>
                    )}

                    <Text style={[styles.itemMeta, { color: themeColors.textMuted }]}>
                      حُذف في: {formatDate(item.deleted_at)}
                      {item.deleted_by_name ? ` — بواسطة ${item.deleted_by_name}` : ''}
                    </Text>

                    <View style={styles.expiryRow}>
                      {item.is_expired ? (
                        <Text style={[styles.expiryText, { color: '#B91C1C' }]}>
                          ⚠ انتهت مدة الاستعادة (سيُحذف نهائياً قريباً)
                        </Text>
                      ) : (
                        <Text style={[
                          styles.expiryText,
                          { color: expiringSoon ? '#92400E' : themeColors.textMuted },
                        ]}>
                          {expiringSoon ? '⏰ ' : ''}
                          متبقي {item.days_until_expiry} يوم على الحذف النهائي
                        </Text>
                      )}
                    </View>

                    {item.reason && (
                      <Text style={[styles.itemMeta, { color: themeColors.textMuted, fontStyle: 'italic' }]}>
                        سبب الحذف: {item.reason}
                      </Text>
                    )}
                  </View>

                  {canRestore && !item.is_expired && !item.is_restored && (
                    <TouchableOpacity
                      style={styles.restoreBtn}
                      onPress={() => setRestoring(item)}
                    >
                      <Text style={styles.restoreBtnText}>استعادة</Text>
                    </TouchableOpacity>
                  )}
                  {item.is_restored && (
                    <View style={styles.restoredBadge}>
                      <Text style={styles.restoredBadgeText}>✓ مُستعاد</Text>
                    </View>
                  )}
                </View>
              );
            })}

            {/* Pagination */}
            <View style={[styles.pagination, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.paginationText, { color: themeColors.textMuted }]}>
                صفحة {page} من {totalPages} — {count} عنصر
              </Text>
              <View style={{ flexDirection: 'row-reverse', gap: 8 }}>
                <TouchableOpacity
                  style={[styles.pageBtn, page <= 1 && { opacity: 0.4 }]}
                  disabled={page <= 1}
                  onPress={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <Text style={styles.pageBtnText}>السابق</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.pageBtn, page >= totalPages && { opacity: 0.4 }]}
                  disabled={page >= totalPages}
                  onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <Text style={styles.pageBtnText}>التالي</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </ScrollView>

      {/* Confirm restore modal */}
      <Modal visible={!!restoring} transparent animationType="fade" onRequestClose={() => !restoringInFlight && setRestoring(null)}>
        {restoring && (
          <View style={styles.modalOverlay}>
            <View style={[styles.modalCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>
                تأكيد الاستعادة
              </Text>
              <Text style={[styles.modalText, { color: themeColors.textMuted }]}>
                هل تريد استعادة <Text style={{ fontFamily: FONTS.medium, color: themeColors.textPrimary }}>
                  "{restoring.object_title || `عنصر #${restoring.object_id}`}"
                </Text>{' '}
                ({CONTENT_TYPE_LABELS[restoring.content_type] || restoring.content_type})؟
              </Text>
              <Text style={[styles.modalText, { color: themeColors.textMuted, marginTop: 8 }]}>
                سيتم إعادة العنصر إلى مكانه الأصلي ويصبح ظاهراً مرة أخرى.
              </Text>

              <View style={{ flexDirection: 'row-reverse', gap: 8, marginTop: 20 }}>
                <TouchableOpacity
                  style={[styles.modalConfirmBtn, restoringInFlight && { opacity: 0.5 }]}
                  disabled={restoringInFlight}
                  onPress={() => handleRestore(restoring)}
                >
                  <Text style={styles.modalConfirmBtnText}>
                    {restoringInFlight ? 'جاري الاستعادة...' : 'استعادة'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalCancelBtn, { borderColor: themeColors.border }]}
                  disabled={restoringInFlight}
                  onPress={() => setRestoring(null)}
                >
                  <Text style={[styles.modalCancelBtnText, { color: themeColors.textPrimary }]}>إلغاء</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  headerCard: {
    flexDirection: 'row-reverse', alignItems: 'center',
    padding: 16, borderRadius: 12, borderWidth: 1,
  },
  title: { fontSize: 20, fontFamily: FONTS.medium, textAlign: 'right' },
  subtitle: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginTop: 4, lineHeight: 20 },

  filterCard: { padding: 16, borderRadius: 12, borderWidth: 1 },
  filterRow: { flexDirection: 'row-reverse', gap: 12, flexWrap: 'wrap' },
  filterField: { flex: 1, minWidth: 200 },
  filterLabel: { fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right', marginBottom: 4 },
  input: {
    borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 13, fontFamily: FONTS.regular, minHeight: 44,
  },

  emptyCard: { padding: 40, borderRadius: 12, borderWidth: 1, alignItems: 'center' },
  emptyText: { fontSize: 14, fontFamily: FONTS.regular },

  itemCard: {
    flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 12,
    padding: 14, borderRadius: 10, borderWidth: 1,
  },
  itemHeaderRow: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  itemTitle: { fontSize: 15, fontFamily: FONTS.medium, textAlign: 'right', flex: 1 },
  itemMeta: { fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right' },
  typeBadge: {
    paddingVertical: 3, paddingHorizontal: 8, borderRadius: 6,
    backgroundColor: '#EFF6FF',
  },
  typeBadgeText: { color: '#1E40AF', fontSize: 11, fontFamily: FONTS.medium },
  expiryRow: { marginTop: 4 },
  expiryText: { fontSize: 12, fontFamily: FONTS.medium, textAlign: 'right' },

  restoreBtn: {
    paddingVertical: 8, paddingHorizontal: 16, borderRadius: 6,
    backgroundColor: '#DCFCE7',
    alignSelf: 'center',
  },
  restoreBtnText: { color: '#15803D', fontSize: 13, fontFamily: FONTS.medium },
  restoredBadge: {
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6,
    backgroundColor: '#F3F4F6',
    alignSelf: 'center',
  },
  restoredBadgeText: { color: '#15803D', fontSize: 12, fontFamily: FONTS.medium },

  pagination: {
    flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between',
    padding: 12, borderRadius: 10, borderWidth: 1,
  },
  paginationText: { fontSize: 12, fontFamily: FONTS.regular },
  pageBtn: {
    paddingVertical: 6, paddingHorizontal: 14,
    backgroundColor: COLORS.primary, borderRadius: 6,
  },
  pageBtnText: { color: '#FFFFFF', fontSize: 12, fontFamily: FONTS.medium },

  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    width: '100%', maxWidth: 480, padding: 24, borderRadius: 12, borderWidth: 1,
  },
  modalTitle: {
    fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right', marginBottom: 12,
  },
  modalText: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', lineHeight: 22 },
  modalConfirmBtn: {
    flex: 1, paddingVertical: 12, backgroundColor: '#16A34A',
    borderRadius: 8, alignItems: 'center',
  },
  modalConfirmBtnText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  modalCancelBtn: {
    flex: 1, paddingVertical: 12, borderWidth: 1,
    borderRadius: 8, alignItems: 'center',
  },
  modalCancelBtnText: { fontSize: 14, fontFamily: FONTS.medium },
});
